import { createApi } from '@reduxjs/toolkit/query/react'
import { BasePageRequest, BasePageResponse, BaseRequest, BaseResponse } from '../models'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'

// Define a service using a base URL and expected endpoints
const _BASE_URL = import.meta.env.REACT_APP_AI_BASE_URL!

export interface GetMeetingTemplatesRequest extends BasePageRequest {
  includeInactive?: boolean
}

export interface GetMeetingTemplateByIdRequest extends BaseRequest {
  meetingTemplateId: string
}

export interface GetDefaultMeetingTemplateRequest extends BaseRequest {
  userId: string | null
}

export interface SetUserDefaultMeetingTemplatePayload {
  meetingTemplateId: string | null
}

export interface SetUserDefaultMeetingTemplateRequest extends BaseRequest {
  payload: SetUserDefaultMeetingTemplatePayload
}

export interface MeetingTemplatePreset {
  aiFeaturePresetId: string
  aiFeatureId?: string
  metadata: string
}

export interface MeetingTemplateDetectionConfig {
  description: string
  averageMeetingTime?: string
  meetingName?: string
}

export interface MeetingTemplate {
  id: string
  name: string
  presets: MeetingTemplatePreset[]
  formPresets: MeetingTemplatePreset[]
  isActive: boolean
  isDefault: boolean
  isPrivate: boolean
  detectionConfig: MeetingTemplateDetectionConfig
  fromSystemMeetingTemplate: boolean
  systemMeetingTemplateId?: string
  metadata: string
}

export interface DefaultMeetingTemplate extends MeetingTemplate {
  isUserDefault: boolean
}

export interface CreateMeetingTemplatePayload {
  name: string
  isActive?: boolean
  isDefault?: boolean
  presets: MeetingTemplatePreset[]
  formPresets: MeetingTemplatePreset[]
  detectionConfig: MeetingTemplateDetectionConfig
}

export interface CreateMeetingTemplateRequest extends BaseRequest {
  payload: CreateMeetingTemplatePayload
}

export interface UpdateMeetingTemplatePayload {
  name?: string
  isActive?: boolean
  isDefault?: boolean
  isPrivate?: boolean
  presets?: MeetingTemplatePreset[]
  formPresets?: MeetingTemplatePreset[]
  detectionConfig?: MeetingTemplateDetectionConfig
  metadata?: string
  changePastMeetings?: boolean
}

export interface UpdateMeetingTemplateRequest extends BaseRequest {
  meetingTemplateId: string
  payload: UpdateMeetingTemplatePayload
}

export interface DeleteMeetingTemplateRequest extends BaseRequest {
  meetingTemplateId: string
}

export const meetingTemplateApi = createApi({
  reducerPath: 'meetingTemplateApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['MeetingTemplate'],
  endpoints: (builder) => ({
    getMeetingTemplates: builder.query<BasePageResponse<MeetingTemplate[]>, GetMeetingTemplatesRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/meeting-templates`,
          params: {
            page: request.pageRequest.page,
            size: request.pageRequest.size,
            includeInactive: request.includeInactive,
          },
        }
      },
      providesTags: ['MeetingTemplate'],
    }),
    getMeetingTemplateById: builder.query<BaseResponse<MeetingTemplate>, GetMeetingTemplateByIdRequest>({
      query: (request) => `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
      providesTags: ['MeetingTemplate'],
    }),
    getDefaultMeetingTemplate: builder.query<DefaultMeetingTemplate, GetDefaultMeetingTemplateRequest>({
      query: (request) => {
        return {
          url: `/accounts/${request.accountId}/meeting-templates/default`,
          params: { userId: request.userId },
        }
      },
      transformResponse: (response: BaseResponse<DefaultMeetingTemplate>) => {
        return response.data
      },
      providesTags: ['MeetingTemplate'],
    }),
    createMeetingTemplate: builder.mutation<MeetingTemplate, CreateMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/`,
        method: 'POST',
        body: request.payload,
      }),
      transformResponse: (response: BaseResponse<MeetingTemplate>) => {
        return response.data
      },
      invalidatesTags: ['MeetingTemplate'],
    }),
    updateMeetingTemplate: builder.mutation<void, UpdateMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
        method: 'PATCH',
        body: request.payload,
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
    deleteMeetingTemplate: builder.mutation<void, DeleteMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/${request.meetingTemplateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
    setUserDefaultMeetingTemplate: builder.mutation<BaseResponse<DefaultMeetingTemplate>, SetUserDefaultMeetingTemplateRequest>({
      query: (request) => ({
        url: `/accounts/${request.accountId}/meeting-templates/set-user-default`,
        method: 'POST',
        body: request.payload,
      }),
      invalidatesTags: ['MeetingTemplate'],
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useGetMeetingTemplatesQuery,
  useLazyGetMeetingTemplatesQuery,
  useGetDefaultMeetingTemplateQuery,
  useLazyGetDefaultMeetingTemplateQuery,
  useGetMeetingTemplateByIdQuery,
  useCreateMeetingTemplateMutation,
  useUpdateMeetingTemplateMutation,
  useDeleteMeetingTemplateMutation,
  useSetUserDefaultMeetingTemplateMutation,
} = meetingTemplateApi
